/**
 * Curated speaker details, keyed by the event they are speaking at.
 *
 * Extracted from FeaturedSpeakers so the event detail page shows the same face the
 * Speakers page does. It previously lived inside that page, which is why an event
 * page fell back to initials for a speaker whose photograph the site was already
 * displaying two clicks away.
 *
 * This is a stopgap by nature: the durable source is a portrait published through the
 * speaker pipeline, which both pages prefer when one exists. This covers the catalog
 * speakers who predate that pipeline.
 */
import { BrainCircuit, Handshake, Mic, Sparkles, type LucideIcon } from "lucide-react";

export type SpeakerProfileDetail = {
  title: string;
  /** The mark shown beside the speaker on the roster card. Every entry has one. */
  icon: LucideIcon;
  /** Initials drawn when there is no photograph. */
  monogram?: string;
  photo?: string;
  bio: string;
  linkedinUrl?: string;
  /**
   * Optional personal or company site. No speaker source currently carries a
   * website field, so this stays undefined until one is published — the follow
   * row simply omits the icon rather than rendering a dead link.
   */
  websiteUrl?: string;
};

export const PROFILE_DETAILS: Record<string, SpeakerProfileDetail> = {
  "advancing-hr-capabilities-with-ai": {
    title: "Chief Executive Officer",
    photo: "/media/speakers/johnathan-tebeau-cutout.png",
    bio: "Johnathan helps leaders use AI to remove administrative friction and create more room for strategic, human-centered work.",
    linkedinUrl: "https://www.linkedin.com/in/jtebeau",
    icon: BrainCircuit,
    monogram: "JT",
  },
  "stop-answering-same-questions-ai-hr-assistant": {
    title: "Founder",
    photo: "/media/speakers/nahed-khairallah-cutout.png",
    bio: "Nahed helps growing organizations turn practical AI into a more useful, human employee experience.",
    linkedinUrl: "https://www.linkedin.com/in/khairallahnahed",
    icon: BrainCircuit,
    monogram: "NK",
  },
  "scaling-hiring-infrastructure-growth": {
    title: "Talent Acquisition Strategist",
    photo: "/media/speakers/michael-goldberg-cutout.png",
    bio: "Michael builds stronger talent acquisition functions that support deliberate, sustainable growth.",
    linkedinUrl: "https://www.linkedin.com/in/dallasmichaelgoldberg",
    icon: Sparkles,
    monogram: "MG",
  },
  "disrupthr-dallas-6": {
    title: "Fast-format thought leadership",
    photo: "/media/speakers/multi-speaker-default.png",
    bio: "A collective of workplace voices bringing bold ideas and meaningful conversation to Dallas.",
    icon: Sparkles,
    monogram: "DH",
  },
  "hard-conversations-every-leader-needs": {
    title: "Leadership Communication Expert",
    photo: "/media/speakers/farrah-mitra-cutout.png",
    bio: "Farrah equips leaders to navigate pivotal conversations with clarity, care, and confidence.",
    linkedinUrl: "https://www.linkedin.com/in/farrah-mitra-executive-coach",
    icon: Mic,
    monogram: "FM",
  },
  "the-human-advantage-building-community-age-ai": {
    title: "Community & culture facilitator",
    photo: "/media/speakers/tiona-nicholas-cutout.png",
    bio: "Tiona explores what keeps connection, empathy, and community at the center of evolving work.",
    linkedinUrl: "https://www.linkedin.com/in/tiona-nicholas-701887178",
    icon: Handshake,
    monogram: "TN",
  },
  "evidence-based-decision-model": {
    title: "CEO Advisor & Executive Coach",
    photo: "/media/speakers/diane-dye-cutout.png",
    bio: "Diane advises founders and executive teams through high-stakes decisions and transformational change.",
    linkedinUrl: "https://www.linkedin.com/in/drdianedye",
    icon: BrainCircuit,
    monogram: "DD",
  },
  "the-secret-to-everyday-confidence": {
    title: "Chief Confidence Igniter",
    photo: "/media/speakers/meshell-baker-portrait.webp",
    bio: "Meshell helps leaders build the everyday confidence needed to act with greater influence and impact.",
    linkedinUrl: "https://www.linkedin.com/in/meshellrbaker",
    icon: Sparkles,
    monogram: "MB",
  },
};

/** The curated portrait for an event's speaker, when the catalog has one. */
export function curatedSpeakerPhoto(eventSlug: string): string | undefined {
  return PROFILE_DETAILS[eventSlug]?.photo;
}
